import type { ProductDetail } from "./types";
import { resolveCheckoutTruckOptions } from "@shared/products/truck-capacity";
import {
  canScheduleTruckloads,
  getCheckoutMinimumQuantity,
  getRecommendedTruckloadQuantity,
} from "@shared/products/checkout";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";
import { LOADING_TRUCK_TYPE } from "@convex/constants/types.lib";

export interface ListingActionPrefill {
  quantity: number;
  deliveryDate: string;
  addressDisplay: string;
  address: {
    line1: string;
    city: string;
    state: string;
    zip: string;
  } | null;
}

export function getInitialListingQuantity(input: {
  quantityValue: string | undefined;
  suggestedQuantity: number;
  checkoutMinimum: number;
}) {
  const parsedQuantity = input.quantityValue
    ? parseInt(input.quantityValue, 10)
    : NaN;
  if (Number.isNaN(parsedQuantity) || parsedQuantity < 1) {
    return normalizeListingQuantity(input.suggestedQuantity);
  }
  return normalizeListingQuantity(
    Math.max(parsedQuantity, input.checkoutMinimum),
  );
}

export function getSuggestedListingQuantity(input: {
  checkoutMinimum: number;
  truckloadCapacity?: number;
}) {
  return normalizeListingQuantity(
    input.truckloadCapacity
      ? getRecommendedTruckloadQuantity(
          input.checkoutMinimum,
          input.truckloadCapacity,
        )
      : input.checkoutMinimum,
  );
}

export interface ListingQuantityPick {
  label: string;
  quantity: number;
}

const LOAD_PICK_LABELS: Partial<Record<string, { half: string; full: string }>> = {
  [LOADING_TRUCK_TYPE.CARGO_VAN]: { half: "Half van", full: "Full van" },
  [LOADING_TRUCK_TYPE.BOX_TRUCK]: { half: "Half box truck", full: "Full box truck" },
};

export function getListingQuantityPicks(input: {
  checkoutMinimum: number;
  truckCapacity: number;
  truckType?: string;
}): ListingQuantityPick[] {
  const labels = (input.truckType ? LOAD_PICK_LABELS[input.truckType] : undefined) ?? { half: "Half truckload", full: "Truckload" };
  const picks: ListingQuantityPick[] = [];
  if (input.checkoutMinimum > 1) {
    picks.push({ label: "Minimum", quantity: input.checkoutMinimum });
  }
  const halfTruck = Math.round(input.truckCapacity / 2);
  if (input.truckCapacity >= 2 && halfTruck > input.checkoutMinimum) {
    picks.push({ label: labels.half, quantity: halfTruck });
  }
  if (input.truckCapacity > input.checkoutMinimum) {
    picks.push({ label: labels.full, quantity: input.truckCapacity });
  }
  return picks;
}

export function normalizeListingQuantity(value: number) {
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.round(value));
}

export function getInitialQuoteQuantity(
  quantityValue: string | undefined,
  minOrderQuantity: number,
) {
  const parsedQuantity = quantityValue ? parseInt(quantityValue, 10) : NaN;
  return Number.isNaN(parsedQuantity)
    ? minOrderQuantity
    : Math.max(parsedQuantity, minOrderQuantity);
}

export function getInitialQuoteDeliveryDate(
  deliveryDate: string | undefined,
  leadTimeDays: number,
): string {
  const requestedWindow = deliveryDate
    ? parseDeliveryWindowParam(`${deliveryDate}_${deliveryDate}`, leadTimeDays)
    : null;
  return requestedWindow?.start ?? defaultDeliveryWindow(leadTimeDays).start;
}
import {
  defaultDeliveryWindow,
  parseDeliveryWindowParam,
} from "@/lib/checkout/delivery-window";

export function getListingPurchasePolicy(
  data: Pick<
    ProductDetail,
    | "minOrderQuantity"
    | "minCheckoutQuantity"
    | "truckCapacities"
    | "productType"
  >,
) {
  const checkoutMinimum = getCheckoutMinimumQuantity(data);
  const truckOptions = resolveCheckoutTruckOptions(
    data.truckCapacities,
    data.productType?.defaultTruckloadQuantities,
  );
  const truck =
    truckOptions.find((option) =>
      canScheduleTruckloads(checkoutMinimum, option.capacity, checkoutMinimum),
    ) ?? truckOptions[0];
  const truckCapacity = truck?.capacity ?? 0;
  const suggestedQuantity = getSuggestedListingQuantity({
    checkoutMinimum,
    truckloadCapacity: truckCapacity,
  });
  return {
    checkoutMinimum,
    truckCapacity,
    truckType: truck?.truckType,
    truckOptions,
    canSchedule: canScheduleTruckloads(
      checkoutMinimum,
      truckOptions,
      suggestedQuantity,
    ),
    suggestedQuantity,
  };
}


export function getListingActionLabel(
  data: Pick<ProductDetail, "intent">,
  isCheckoutReady: boolean,
) {
  if (isCheckoutReady) return "View options";
  if (data.intent === "recycle") return "Respond to this listing";
  return CHECK_AVAILABILITY_LABEL;
}
